import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { normaliseScorecard, nextLevel } from '../utils/score';
import { IconChartLine, IconClipboard } from '../components/dashboardIcons';
import { elementMeta } from './components/elementMeta';
import ElementIcon from '../components/ElementIcon';
import OutstandingNote from '../components/OutstandingNote';
import ThemeSwitch from '../components/ThemeSwitch';
import { themeVars } from '../config/theme';
import { buildOutstanding } from '../utils/outstanding';
import { useDocumentProgress } from '../utils/useDocumentProgress';
import { getChecklist } from '../config/documentChecklists';
import { scrollBehavior } from './clientUi';
import ElementCard from './components/ElementCard';
import ElementPanel from './components/ElementPanel';
import ClientSkeleton from './components/ClientSkeleton';
import YesCard, { YesHeroBadge } from './components/YesCard';
import FullReport from './components/FullReport';
import ScenarioPanel from './components/ScenarioPanel';
import './client.scss';

const ALL_KEY = '__all__';
const SCENARIO_KEY = '__scenario__';

const fmt = (n) => (Math.round(n * 100) / 100).toFixed(2);

// Elements whose scorecard carries an extra summary table, and the response field it lives in.
// Older stored scorecards may predate the field — if it's missing we recompute from the data sheet.
const SUMMARY_FIELD = {
  procurement: 'pp_spend_summary',
  sed: 'sed_spend_summary',
  sed_transport: 'sed_spend_summary',
  enterprise_development: 'spend_summary',
  supplier_development: 'spend_summary',
  enterprise_development_transport: 'spend_summary',
  skills_development: 'spend_summary',
  skills_development_transport: 'spend_summary',
  skills_development_specialised: 'spend_summary',
  management_control: 'headcount_summary',
  // management_control_transport has no headcount table (the Road Freight MC sheet is board and
  // top management only), so it is left out to avoid recalculating it on every load.
  employment_equity: 'headcount_summary',
};
const hasField = (card, field) => Array.isArray(card?.[field]) && card[field].length > 0;

const initials = (name = '') =>
  name.split(/[\s._-]+/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join('') || '·';

const ClientDashboard = ({ companies = [], companyId, financialYear, onCompanyChange, username, onLogout }) => {
  const [loading, setLoading] = useState(true);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [beeLevel, setBeeLevel] = useState(null);
  const [groups, setGroups] = useState([]);
  const [scorecards, setScorecards] = useState({});
  const [open, setOpen] = useState(null); // { key, view }
  const [refreshKey, setRefreshKey] = useState(0);
  const [showTop, setShowTop] = useState(false);
  const reload = () => setRefreshKey((k) => k + 1);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 400);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Opening or closing a panel returns you to the top.
  useEffect(() => { window.scrollTo({ top: 0, behavior: scrollBehavior() }); }, [open]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setScorecards({});
      const [infoRes, totalRes, elementsRes] = await Promise.allSettled([
        api.get('/core/company-info/'),
        api.get('/core/scorecard/combined-total/'),
        api.get('/core/scorecard/list_elements/'),
      ]);
      if (cancelled) return;
      if (infoRes.status === 'fulfilled') setCompanyInfo(infoRes.value.data);
      if (totalRes.status === 'fulfilled') setBeeLevel(totalRes.value.data);

      const rawGroups = elementsRes.status === 'fulfilled' ? elementsRes.value.data : [];
      const allIds = rawGroups.flatMap((g) => g.elements);
      const cards = {};
      const accessible = new Set();
      const recalc = (id) => api.post('/core/scorecard/calculate_from_data_sheet/', { element: id }).then((r) => r.data);

      await Promise.all(allIds.map(async (id) => {
        try {
          const res = await api.get(`/core/scorecard/get_scorecard/?element=${id}`);
          cards[id] = res.data;
          accessible.add(id);
          // Stored record predates the summary-table field — refresh it from the data sheet.
          const field = SUMMARY_FIELD[id];
          if (field && !hasField(cards[id], field)) {
            try { cards[id] = await recalc(id); } catch { /* keep get_scorecard data */ }
          }
        } catch (err) {
          if (err.response?.status !== 404) return; // 403 / other → not visible to this role
          accessible.add(id);
          // No stored scorecard — try recomputing from the last uploaded data sheet
          // (matches the consultant scorecard pages; also covers supplier_development,
          // whose record only exists after an ESD calculation).
          try {
            cards[id] = await recalc(id);
          } catch {
            cards[id] = err.response.data || { indicators: [] };
          }
        }
      }));
      if (cancelled) return;
      const visibleGroups = rawGroups
        .map((g) => ({ ...g, elements: g.elements.filter((id) => accessible.has(id)) }))
        .filter((g) => g.elements.length > 0);
      setScorecards(cards);
      setGroups(visibleGroups);
      setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [companyId, refreshKey]);

  const groupScore = (group) => {
    const parts = group.elements.map((id) => normaliseScorecard(scorecards[id])).filter(Boolean);
    if (parts.length === 0) return null;
    return {
      points: parts.reduce((s, p) => s + p.points, 0),
      maxPoints: parts.reduce((s, p) => s + p.maxPoints, 0),
      subMinimumMet: parts.every((p) => p.subMinimumMet !== false),
    };
  };

  // Verification-document progress per element (shared with the consultant Home).
  const docProgress = useDocumentProgress(
    groups.map((g) => ({ key: g.key, elementId: g.elements[0] })),
    refreshKey,
  );

  // Sticky-note items, built only from what the backend returned.
  const outstanding = loading ? [] : buildOutstanding(
    groups.map((g) => ({ key: g.key, label: g.label, score: groupScore(g) })),
    docProgress,
  );
  const openOutstanding = (item) => setOpen({
    key: item.key,
    view: item.kind === 'docs' ? 'documents' : item.kind === 'data' ? 'upload' : 'overview',
  });

  // groups arrive from /core/scorecard/list_elements/ already in the canonical
  // consultant order (ELEMENT_GROUPS); the "elements needing action" tile surfaces
  // priorities without reordering the grid.
  const attnGroups = groups.filter((g) => groupScore(g)?.subMinimumMet === false);

  const companyName = companyInfo?.company_name || companies.find((c) => c.id === companyId)?.name || 'Your company';
  const days = companyInfo?.days_to_verification;

  const levelMatch = /(\d+)/.exec(beeLevel?.level || '');
  const levelNum = levelMatch ? levelMatch[1] : (beeLevel?.level ? 'NC' : '—');
  const trackPct = beeLevel && beeLevel.max_points
    ? Math.min((beeLevel.combined_points / beeLevel.max_points) * 100, 100)
    : 0;
  const nl = beeLevel ? nextLevel(beeLevel.combined_points, beeLevel.thresholds) : null;

  const showFullReport = open?.key === ALL_KEY;
  const showScenario = open?.key === SCENARIO_KEY;
  const openGroup = open && !showFullReport && !showScenario ? groups.find((g) => g.key === open.key) : null;

  return (
    <div className="cd-app">
      <header className="cd-topbar">
        <div className="cd-brand"><i /> NPI B-BBEE dashboard</div>
        <div className="cd-topbar-right">
          <ThemeSwitch />
          {companies.length > 1 ? (
            <select className="cd-switch" value={companyId} onChange={(e) => onCompanyChange(e.target.value)}>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          ) : (
            <span className="cd-switch">{companyName}</span>
          )}
          <span className="cd-avatar" title={username}>
          <span aria-hidden="true">{initials(username)}</span>
          <span className="cd-visually-hidden">Signed in as {username}</span>
        </span>
          <button type="button" className="cd-btn cd-btn--ghost" onClick={onLogout}>Log out</button>
        </div>
      </header>

      <div className="cd-wrap">
        <section className="cd-hero">
          <div className="cd-hero-main">
            <div className="cd-eyebrow">{companyName} · {financialYear?.label || 'Current year'}</div>
            <div className="cd-bignum"><b>{levelNum}</b><em>B-BBEE level</em></div>
            <div className="cd-track">
              <i style={{ width: `${trackPct}%` }} />
              {beeLevel?.max_points > 0 && beeLevel.thresholds?.map((t) => (
                <span
                  key={t.label}
                  className={`cd-level-tick${nl?.label === t.label ? ' is-next' : ''}`}
                  style={{ left: `${Math.min((t.min / beeLevel.max_points) * 100, 100)}%` }}
                >
                  <span>{t.label.replace('Level ', 'L')}</span>
                </span>
              ))}
            </div>
            <p className="cd-subline">
              {beeLevel
                ? <><b>{fmt(beeLevel.combined_points)}</b> of {beeLevel.max_points} points achieved{nl ? ` · ${fmt(nl.needed)} points to ${nl.label}` : ''}</>
                : 'Upload your data sheets to calculate your scorecard.'}
            </p>

            {beeLevel?.yes && <YesHeroBadge yes={beeLevel.yes} level={beeLevel.level} />}

            {!loading && groups.length > 0 && (
              <div className="cd-docprog">
                <div className="cd-docprog-head">Verification documents</div>
                <div className="cd-docprog-rings">
                  {groups.map((g) => {
                    const dp = docProgress[g.key];
                    const total = dp?.total ?? getChecklist(g.elements[0]).length;
                    const done = dp?.done ?? 0;
                    return (
                      <button
                        key={g.key}
                        type="button"
                        className="cd-docbtn themed"
                        style={themeVars(g.key)}
                        title={`${g.label}: ${done}/${total} documents`}
                        aria-label={`${g.label}: ${done} of ${total} verification documents uploaded`}
                        onClick={() => setOpen({ key: g.key, view: 'documents' })}
                      >
                        <ElementIcon element={g.key} />
                        <span>{g.label}</span>
                        <span className="n">{done}/{total}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="cd-statcol">
            <div className="cd-stat">
              <div className="k">Combined points</div>
              <div className="v">{beeLevel ? fmt(beeLevel.combined_points) : '—'} <small>/ {beeLevel ? beeLevel.max_points : '—'}</small></div>
            </div>
            <div className={`cd-stat ${attnGroups.length ? 'warn' : ''}`}>
              <div className="k">Elements needing action</div>
              <div className="v">{loading ? '—' : attnGroups.length}</div>
              {attnGroups.length > 0 && (
                <div className="cd-stat-tags">
                  {attnGroups.map((g) => (
                    <button key={g.key} type="button" className="cd-attn-tag" onClick={() => setOpen({ key: g.key, view: 'overview' })}>
                      {g.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="cd-stat">
              <div className="k">Verification window opens</div>
              <div className="v">{days == null ? '—' : days < 0 ? 'Overdue' : days} {days != null && days >= 0 && <small>days</small>}</div>
            </div>
          </div>
        </section>

        {!loading && !open && (
          <OutstandingNote
            title="What we need from you"
            items={outstanding}
            daysToVerification={days}
            onSelect={openOutstanding}
            collapsible
          />
        )}

        <div className="cd-sec">
          <h2>Your scorecard</h2>
          <span className="hint">
            {open ? 'Press Esc or × to go back' : 'Priority elements first · click a card to open it in place'}
          </span>
        </div>

        {loading ? (
          <ClientSkeleton />
        ) : showFullReport ? (
          <FullReport
            groups={groups}
            scorecards={scorecards}
            companyName={companyName}
            financialYear={financialYear}
            beeLevel={beeLevel}
            onClose={() => setOpen(null)}
          />
        ) : showScenario ? (
          <ScenarioPanel
            companyName={companyName}
            financialYear={financialYear}
            beeLevel={beeLevel}
            onClose={() => setOpen(null)}
          />
        ) : openGroup ? (
          <ElementPanel
            key={openGroup.key}
            group={openGroup}
            accent={elementMeta(openGroup.key).accent}
            score={groupScore(openGroup)}
            scorecards={scorecards}
            companyName={companyName}
            financialYear={financialYear}
            beeLevel={beeLevel}
            initialView={open.view}
            onClose={() => setOpen(null)}
            onCalculated={reload}
          />
        ) : groups.length === 0 ? (
          <div className="cd-empty">
            <span className="ic"><IconClipboard /></span>
            <h3>No scorecard elements yet</h3>
            <p>Your account isn’t linked to any scorecard elements. Contact your consultant to get set up.</p>
          </div>
        ) : (
          <div className="cd-cards">
            {groups.map((group) => (
              <ElementCard
                key={group.key}
                group={group}
                score={groupScore(group)}
                onOpen={(view) => setOpen({ key: group.key, view })}
              />
            ))}
            {beeLevel?.yes && <YesCard yes={beeLevel.yes} level={beeLevel.level} />}
            <div className="cd-report-card">
              <div className="ri"><IconChartLine /></div>
              <div className="rt">
                <b>Full B-BBEE report</b>
                <p>Every element in one PDF — ready for your verification agency.</p>
              </div>
              <button type="button" className="cd-btn cd-btn--ghost"
                onClick={() => setOpen({ key: ALL_KEY })}>
                Open report
              </button>
            </div>
          </div>
        )}
      </div>

      {showTop && (
        <button
          type="button"
          className="cd-scrolltop"
          onClick={() => window.scrollTo({ top: 0, behavior: scrollBehavior() })}
          aria-label="Back to top"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="18 15 12 9 6 15" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default ClientDashboard;
